// Tabla de horaario de turnos

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SaveIcon from '@mui/icons-material/Save';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import isoWeek from "dayjs/plugin/isoWeek";
import { apiFetch, apiFetchJson } from '../fetchWrapper';
import ClientSelector from './ClientSelector';

dayjs.extend(isoWeek);
dayjs.locale("es");

export default function ShiftSchedulerTable() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [sites, setSites] = useState([]);
  const [clientSites, setClientSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [scheduledShifts, setScheduledShifts] = useState([]);
  const [siteActiveTemplates, setSiteActiveTemplates] = useState({});
  const [assignments, setAssignments] = useState({});
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientSupervisors, setSelectedClientSupervisors] = useState([]);
  const [savingClientSupervisors, setSavingClientSupervisors] = useState(false);

  const startOfWeekKey = selectedDate.startOf("isoWeek").format("YYYY-MM-DD");
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    dayjs(startOfWeekKey).add(i, "day")
  );

  // Todos los datos necesarios para la tabla
  useEffect(() => {
    apiFetchJson("/api/sites").then(data => setSites(Array.isArray(data) ? data : []));
    apiFetchJson("/api/employees").then(data => setEmployees(Array.isArray(data) ? data : []));
    apiFetchJson("/api/shift-templates").then(data => setShiftTemplates(Array.isArray(data) ? data : []));
    apiFetchJson("/api/scheduled-shifts").then(data => setScheduledShifts(Array.isArray(data) ? data : []));
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setClientSites([]);
      setSelectedClientSupervisors([]);
      return;
    }

    apiFetchJson(`/api/clients/${selectedClientId}/sites`)
      .then(data => setClientSites(Array.isArray(data) ? data : []));

    apiFetchJson(`/api/clients/${selectedClientId}/supervisors`)
      .then(data => {
        const supervisors = Array.isArray(data) ? data : [];
        setSelectedClientSupervisors(supervisors.map(s => s.id));
      });
  }, [selectedClientId]);

  // Configurar plantillas activas por ubicación
  useEffect(() => {
    const activeSites = selectedClientId ? clientSites : sites;
    if (!activeSites.length || !shiftTemplates.length) {
      setSiteActiveTemplates({});
      return;
    }

    const allowedTemplatesPerSite = {};
    activeSites.forEach(site => {
      if (site.client.id == 1) { // gosen
        allowedTemplatesPerSite[site.id] = shiftTemplates.filter(t => t.id === 1).map(t => t.id);
      } else {
        allowedTemplatesPerSite[site.id] = shiftTemplates.filter(t => t.id !== 1).map(t => t.id);
      }
    });

    setSiteActiveTemplates(allowedTemplatesPerSite);
  }, [selectedClientId, clientSites, sites, shiftTemplates]);


  // turnos de la semana
  useEffect(() => {
    if (!Array.isArray(scheduledShifts) || scheduledShifts.length === 0) return;

    const endOfWeekKey = dayjs(startOfWeekKey).add(6, "day").format("YYYY-MM-DD");
    const initialAssignments = {};

    scheduledShifts.forEach(shift => {
      const shiftDateKey = dayjs(shift.shiftDate).format("YYYY-MM-DD");

      // solo turnos de esta semana
      if (shiftDateKey < startOfWeekKey || shiftDateKey > endOfWeekKey) {
        return;
      }

      const siteId = shift.siteId;
      const elementIndex = shift.elementIndex || 0; // default to first element if not specified
      const templateId = shift.shiftTemplateId;
      const employeeId = shift.employeeId;

      if (!initialAssignments[siteId]) initialAssignments[siteId] = {};
      if (!initialAssignments[siteId][elementIndex]) initialAssignments[siteId][elementIndex] = {};
      if (!initialAssignments[siteId][elementIndex][shiftDateKey]) initialAssignments[siteId][elementIndex][shiftDateKey] = {};

      initialAssignments[siteId][elementIndex][shiftDateKey][templateId] = employeeId;
      if (shift.supervisorId) {
        initialAssignments[siteId][elementIndex][shiftDateKey].supervisorId = shift.supervisorId;
      }
    });
    setAssignments(initialAssignments);
  }, [scheduledShifts, startOfWeekKey]);


  const handleAssignmentChange = (siteId, elementIndex, dateKey, templateId, employeeId) => {
    setAssignments(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [elementIndex]: {
          ...prev[siteId]?.[elementIndex],
          [dateKey]: {
            ...prev[siteId]?.[elementIndex]?.[dateKey],
            [templateId]: employeeId,
          },
        },
      },
    }));
  };

  const existingShiftMap = useMemo(() => {
    const map = {};
    if (!Array.isArray(scheduledShifts)) return map;

    scheduledShifts.forEach(shift => {
      const elementIndex = shift.elementIndex || 0;
      const key = `${shift.siteId}_${elementIndex}_${shift.shiftTemplateId}_${dayjs(shift.shiftDate).format("YYYY-MM-DD")}`;
      map[key] = shift;
    });
    return map;
  }, [scheduledShifts]);

  const officeEmployees = useMemo(() => {
    if (!employees) return [];
    return employees.filter(e => e.user.role !== "EMPLOYEE");
  }, [employees]);

  const onFieldEmployees = useMemo(() => {
    if (!employees) return [];
    return employees.filter(e => e.user.role === "EMPLOYEE");
  }, [employees]);

  const supervisors = useMemo(() => {
    if (!employees) return [];
    return employees.filter(e => e.user.role === "SUPERVISOR");
  }, [employees]);

  const handleSave = async () => {
    const payload = [];

    Object.entries(assignments).forEach(([siteId, elements]) => {
      Object.entries(elements).forEach(([elementIndex, dates]) => {
        Object.entries(dates).forEach(([shiftDate, dateAssignments]) => {
          const supervisorId = dateAssignments?.supervisorId || null;
          Object.entries(dateAssignments).forEach(([shiftTemplateId, employeeId]) => {
            const parsedTemplateId = Number(shiftTemplateId);
            if (!Number.isFinite(parsedTemplateId)) return;

            const key = `${siteId}_${elementIndex}_${parsedTemplateId}_${shiftDate}`;
            const existing = existingShiftMap[key];

            if (!existing || existing.employeeId !== employeeId || existing.supervisorId !== supervisorId) {
              payload.push({
                id: existing ? existing.id : null,
                siteId: parseInt(siteId),
                elementIndex: parseInt(elementIndex),
                shiftDate,
                shiftTemplateId: parsedTemplateId,
                employeeId,
                supervisorId,
                status: "PLANNED",
                notes: "n/a"
              });
            }
          });
        });
      });
    });

    try {
      await apiFetch("/api/scheduled-shifts", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      alert("Guardado con éxito");
    } catch (err) {
      console.error(err);
      alert("Error al guardar");
    }
  };

  const getCellColor = (siteId, elementIndex, dateKey, templateId) => {
    const employeeId = assignments[siteId]?.[elementIndex]?.[dateKey]?.[templateId];
    if (!employeeId) return "#fff3f3"; // rojo suave (faltante)

    return "#e8f5e9"; // verde suave (asignado)
  };

  const templateMap = useMemo(() => {
    const map = {};
    shiftTemplates.forEach(t => {
      map[t.id] = t;
    });
    return map;
  }, [shiftTemplates]);

  const activeSites = selectedClientId ? clientSites : sites;

  const sortedSites = useMemo(() => {
    if (!activeSites) return [];

    return [...activeSites].sort((a, b) => {
      if (a.client.id === 1 && b.client.id !== 1) return -1;
      if (a.client.id !== 1 && b.client.id === 1) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [activeSites]);

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mb: 2
      }}>
        <Typography variant="h6">Asignación de Turnos</Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box display="flex" gap={2}>
              <DatePicker
                label="Seleccionar semana"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                slotProps={{ textField: { size: "small" } }}
              />
              <Typography variant="body2" sx={{ alignSelf: "center" }}>
                Semana: {dayjs(startOfWeekKey).format("DD MMM")} -{" "}
                {dayjs(startOfWeekKey).add(6, "day").format("DD MMM YYYY")}
              </Typography>
            </Box>
          </LocalizationProvider>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}>
            Guardar
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ClientSelector onSelectClientId={(clientId) => {
          setSelectedClientId(clientId);
          setAssignments({});
        }} />

        {selectedClientId ? (
          <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
            <Typography variant="subtitle1">Supervisores asignados al cliente</Typography>
            <FormControl fullWidth>
              <InputLabel id="client-supervisor-label">Supervisores</InputLabel>
              <Select
                labelId="client-supervisor-label"
                multiple
                value={selectedClientSupervisors}
                onChange={(e) => {
                  const values = Array.isArray(e.target.value) ? e.target.value.map(v => Number(v)) : [];
                  setSelectedClientSupervisors(values);
                }}
                renderValue={(selected) => selected
                  .map(id => supervisors.find(emp => emp.id === Number(id)))
                  .filter(Boolean)
                  .map(emp => `${emp.user.person.firstname} ${emp.user.person.lastnameFirst}`)
                  .join(', ')
                }
                label="Supervisores"
              >
                {supervisors.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    <Checkbox checked={selectedClientSupervisors.includes(emp.id)} />
                    <ListItemText primary={`${emp.user.person.firstname} ${emp.user.person.lastnameFirst}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="secondary"
              onClick={async () => {
                if (!selectedClientId) return;
                setSavingClientSupervisors(true);
                try {
                  await apiFetch(`/api/clients/${selectedClientId}/supervisors`, {
                    method: 'PUT',
                    body: JSON.stringify({ employeeIds: selectedClientSupervisors }),
                  });
                  alert('Supervisores del cliente guardados');
                } catch (err) {
                  console.error(err);
                  alert('Error al guardar supervisores del cliente');
                } finally {
                  setSavingClientSupervisors(false);
                }
              }}
              disabled={savingClientSupervisors}
            >
              Guardar supervisores del cliente
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: '#555' }}>
            Seleccione un cliente para asignar supervisores y luego configure los turnos por sitio.
          </Typography>
        )}
      </Box>

      <Box>
        {selectedClientId && sortedSites.length === 0 && (
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            No se encontraron sitios para el cliente seleccionado.
          </Typography>
        )}

        <TableContainer
          component={Paper}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            maxWidth: "100%",
            position: "relative"
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 900,
              borderCollapse: "separate",
              tableLayout: "auto"
            }}
          >
            <TableHead
              sx={{
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                color: '#333'
              }}>
              <TableRow>
                <TableCell
                  sx={{
                    width: 125,
                    minWidth: 125,
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 10
                  }}>Ubicación</TableCell>

                <TableCell
                  sx={{
                    width: 80,
                    minWidth: 80,
                    position: "sticky",
                    left: 125,
                    backgroundColor: "#f5f5f5",
                    zIndex: 10
                  }}
                >Turnos</TableCell>
                {weekDays.map(day => (
                  <TableCell key={day.format("YYYY-MM-DD")} sx={{ width: 120, textAlign: "center" }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption">{day.format("ddd")}</Typography>
                      <Typography variant="subtitle2">{day.format("DD")}</Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedSites.map((site, siteIndex) => {
                // Create an array of element indices (0 to elementsNumber-1)
                const elementIndices = Array.from({ length: site.elementsNumber || 1 }, (_, i) => i);

                const siteRows = elementIndices.map(elementIndex => (
                  <TableRow key={`${site.id}_${elementIndex}`}>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 5
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{site.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Elemento {elementIndex + 1}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 125,
                        backgroundColor: "#fff",
                        zIndex: 5
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-evenly"
                        height="100%"
                        minHeight="80px"
                      >
                        {siteActiveTemplates[site.id]?.map(templateId => {
                          const template = templateMap[templateId];
                          if (!template) return null;
                          return (
                            <Typography
                              key={template.id}
                              variant="caption"
                              sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                            >
                              {template.name}
                            </Typography>
                          );
                        })}
                      </Box>
                    </TableCell>

                    {weekDays.map(day => {
                      const dateKey = day.format("YYYY-MM-DD");

                      return (
                        <TableCell key={dateKey} sx={{ backgroundColor: '#fff' }}>
                          <Box display="flex" flexDirection="column" gap={0.25}>
                            {siteActiveTemplates[site.id]?.map(templateId => {
                              const template = templateMap[templateId];
                              if (!template) return null;

                              return (
                                <Select
                                  key={template.id}
                                  size="small"
                                  displayEmpty
                                  value={assignments[site.id]?.[elementIndex]?.[dateKey]?.[template.id] || ""}
                                  onChange={e =>
                                    handleAssignmentChange(site.id, elementIndex, dateKey, template.id, e.target.value)
                                  }
                                  sx={{
                                    minHeight: 32,
                                    fontSize: '0.75rem',
                                    width: "100%",
                                    backgroundColor: getCellColor(site.id, elementIndex, dateKey, template.id)
                                  }}
                                >
                                  <MenuItem value="">Sin asignar</MenuItem>

                                  {(template.id === 1 ? officeEmployees : onFieldEmployees).map(emp => (
                                    <MenuItem key={emp.id} value={emp.id}>
                                      {emp.user.person.firstname} {emp.user.person.lastnameFirst}
                                    </MenuItem>
                                  ))}
                                </Select>
                              );
                            })}

                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ));

                // Agregar una fila separadora entre sitios (excepto después del último)
                const separatorRow = siteIndex < sortedSites.length - 1 ? (
                  <TableRow key={`separator_${site.id}`}>
                    <TableCell
                      colSpan={weekDays.length + 2}
                      sx={{
                        padding: '2px',
                        backgroundColor: '#f0f0f0'
                      }}>
                    </TableCell>
                  </TableRow>
                ) : null;
                return [...siteRows, separatorRow].filter(Boolean);
              }).flat()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box >
  );
}
